import { motion } from "framer-motion";
import { Star, ExternalLink, PenLine } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BRAND } from "@/lib/brand";

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.55, ease: "easeOut" },
};

// Google logo SVG (official 4-color G)
const GoogleG = ({ className = "h-4 w-4" }) => (
  <svg viewBox="0 0 48 48" className={className} aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
  </svg>
);

const REVIEWS = [
  {
    name: "Ratul Deka",
    initial: "R",
    color: "bg-blue-500",
    location: "Warehouse Owner · Six Mile",
    rating: 5,
    when: "2 weeks ago",
    text: "Our godown in Six Mile had two break-in attempts last year. Since Secure Watch started monitoring, zero incidents. Their operators actually respond within a minute — worth every rupee.",
  },
  {
    name: "Pinku Barman",
    initial: "P",
    color: "bg-orange-500",
    location: "Retailer · Zoo Road",
    rating: 5,
    when: "1 month ago",
    text: "They called me within 40 seconds of a suspicious person entering my shop after closing. Absolutely brilliant service — highly recommended for shop owners in Guwahati.",
  },
  {
    name: "Sanjeev Choudhury",
    initial: "S",
    color: "bg-emerald-500",
    location: "Homeowner · Beltola",
    rating: 5,
    when: "3 weeks ago",
    text: "I travel for work every week. The daily report gives me total confidence my family is safe at home. Best decision I made this year for home security.",
  },
  {
    name: "Meera Bhattacharya",
    initial: "M",
    color: "bg-rose-500",
    location: "Senior Citizen Care · Chandmari",
    rating: 5,
    when: "1 month ago",
    text: "Signed up for the senior citizen safety plan for my elderly parents. The SOS button and daily wellness check-ins have been a huge relief. Very professional team.",
  },
];

// Verified Google Business listing (CID = 0x9f1f1203f434b9fa from BRAND.mapsUrl)
const BUSINESS_CID = "11466040428841369594";
const BUSINESS_FTID = "0x375a59511af1eac5:0x9f1f1203f434b9fa";
const BUSINESS_QUERY = "Secure+Watch+Six+Mile+Guwahati";
// Google Business Profile reviews (opens the Google Search knowledge panel with the
// reviews sidebar expanded — NOT Google Maps)
const REVIEWS_LINK = `https://www.google.com/search?q=${BUSINESS_QUERY}&ludocid=${BUSINESS_CID}#lrd=${BUSINESS_FTID},1`;
// Write-a-review deep link — opens the Business Profile with the 5-star composer
const WRITE_REVIEW_LINK = `https://www.google.com/search?q=${BUSINESS_QUERY}&ludocid=${BUSINESS_CID}#lrd=${BUSINESS_FTID},3,,,,`;

export default function GoogleReviews() {
  return (
    <section id="reviews" className="relative border-t border-white/10 bg-[#0B0B0E] py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <motion.div {...fadeUp} className="grid gap-10 lg:grid-cols-12 items-end">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1.5">
              <GoogleG className="h-4 w-4" />
              <span className="text-xs font-semibold text-white">
                Reviews <span className="text-white/50">on</span> Google
              </span>
            </div>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white">
              Trusted by people <span className="text-brand-cyan text-glow">across Guwahati</span>.
            </h2>
          </div>

          {/* Rating overview */}
          <motion.div {...fadeUp} className="lg:col-span-5">
            <div className="rounded-2xl border border-white/10 bg-[#0F0F13] p-6 flex items-center gap-6">
              <div className="text-center">
                <div className="font-heading text-5xl font-extrabold text-white leading-none">5.0</div>
                <Stars value={5} className="mt-2" />
                <div className="text-[11px] text-white/50 mt-1 uppercase tracking-widest">Based on Google</div>
              </div>
              <div className="flex-1 space-y-1.5">
                {[5, 4, 3, 2, 1].map((s) => (
                  <div key={s} className="flex items-center gap-2 text-xs">
                    <span className="w-3 text-white/60">{s}</span>
                    <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                    <div className="flex-1 h-1.5 rounded-full bg-white/10 overflow-hidden">
                      <div
                        className="h-full bg-yellow-400"
                        style={{ width: s === 5 ? "100%" : "0%" }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Reviews cards */}
        <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-4" data-testid="google-reviews-grid">
          {REVIEWS.map((r, i) => (
            <motion.article
              key={r.name}
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: i * 0.06 }}
              data-testid={`review-card-${i}`}
              className="group relative rounded-2xl border border-white/10 bg-[#0F0F13] p-5 flex flex-col hover:border-white/25 hover:-translate-y-0.5 transition-all"
            >
              <div className="flex items-start gap-3">
                <div className={`h-10 w-10 shrink-0 rounded-full ${r.color} text-white font-heading font-bold text-lg flex items-center justify-center`}>
                  {r.initial}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-white font-semibold text-sm truncate">{r.name}</div>
                  <div className="text-[11px] text-white/50 truncate">{r.location}</div>
                </div>
                <GoogleG className="h-4 w-4 mt-1 opacity-70" />
              </div>

              <div className="mt-3 flex items-center gap-2">
                <Stars value={r.rating} />
                <span className="text-[11px] text-white/45">{r.when}</span>
              </div>

              <p className="mt-3 text-sm text-white/75 leading-relaxed flex-1">"{r.text}"</p>

              <div className="mt-4 pt-3 border-t border-white/5 text-[10px] text-white/40 uppercase tracking-widest flex items-center gap-1">
                <GoogleG className="h-3 w-3" /> Posted on Google
              </div>
            </motion.article>
          ))}
        </div>

        {/* CTAs */}
        <motion.div
          {...fadeUp}
          className="mt-12 rounded-2xl border border-white/10 bg-gradient-to-r from-brand-cyan/10 via-[#0F0F13] to-[#0F0F13] p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
        >
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-xl bg-white p-2 shrink-0 shadow-[0_0_20px_rgba(255,255,255,0.15)]">
              <GoogleG className="h-full w-full" />
            </div>
            <div>
              <p className="text-white font-semibold">Loved our service?</p>
              <p className="text-white/60 text-sm mt-1">
                A quick 5-star review on Google helps other families &amp; businesses in Guwahati find us.
              </p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <a
              href={REVIEWS_LINK}
              target="_blank"
              rel="noreferrer noopener"
              data-testid="google-reviews-read"
            >
              <Button variant="outline" className="w-full sm:w-auto border-white/20 text-white hover:bg-white/10 font-semibold">
                <ExternalLink className="mr-2 h-4 w-4" />
                Read all reviews
              </Button>
            </a>
            <a
              href={WRITE_REVIEW_LINK}
              target="_blank"
              rel="noreferrer noopener"
              data-testid="google-reviews-write"
            >
              <Button className="w-full sm:w-auto bg-brand-cyan text-black hover:bg-brand-cyan/90 font-bold shadow-[0_0_20px_rgba(0,229,255,0.3)]">
                <PenLine className="mr-2 h-4 w-4" />
                Write a review
              </Button>
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function Stars({ value = 5, className = "" }) {
  return (
    <div className={`flex items-center gap-0.5 ${className}`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={`h-3.5 w-3.5 ${
            n <= value ? "fill-yellow-400 text-yellow-400" : "text-white/20"
          }`}
        />
      ))}
    </div>
  );
}
